/**
 * Orientação de exibição de grade de mapa/compensação — implementa a ordem do contrato 2
 * (`docs/Contrato técnico de mapas tabelas e logs para o front.md`, §5.2 e §10).
 *
 * ## Invariante do contrato (§10)
 *
 * `rpm[]` = linhas, `load[]` = colunas, `values_ms[rpm][load]`. Essa é a ordem de
 * **armazenamento** — sempre canônica, **independente dos flags**. Os três flags
 * (`inverted_axis`, `inverted_rows`, `inverted_cols`) dizem apenas como **exibir**.
 *
 * ## Ordem obrigatória (§5.2)
 *
 * 1. `inverted_axis` → **transpõe** a grade exibida (linhas viram colunas);
 * 2. `inverted_rows` → reverte as **linhas exibidas** (já pós-transposição);
 * 3. `inverted_cols` → reverte as **colunas exibidas**.
 *
 * Cada reversão move **rótulo do eixo e matriz juntos** — o contrato (§5.2) proíbe
 * reordenar `values_ms` sem reordenar o eixo. Por isso a transformação é puramente de
 * vista: nenhum valor muda de célula lógica.
 *
 * ## Armadilha (§5.2)
 *
 * `inverted_rows` **não** é "inverter o eixo de RPM". Com `inverted_axis: true` (o caso
 * de fábrica do Vision) as linhas exibidas são o eixo de **carga**, então é sobre a carga
 * que ele age. Era exatamente esse o bug anterior desta tela.
 *
 * PowerFT vem com os três flags sempre `false` (normalizado no servidor); Vision reflete
 * o arquivo e vem de fábrica com `inverted_axis: true` **e** `inverted_rows: true`.
 */

/** Grandeza física representada por um eixo exibido. */
export type GridQuantity = 'rpm' | 'load';

export interface OrientedGrid {
  /** Rótulos do eixo VERTICAL exibido, em ordem de leitura (topo → base). */
  rowLabels: number[];
  /** Rótulos do eixo HORIZONTAL exibido, da esquerda para a direita. */
  colLabels: number[];
  /** Valores na mesma ordem de `rowLabels` × `colLabels`. */
  grid: number[][];
  /** Grandeza do eixo vertical exibido. */
  rowIs: GridQuantity;
  /** Grandeza do eixo horizontal exibido. */
  colIs: GridQuantity;
}

/** Entrada mínima — serve para `InjectionMap` e para `CompensationTable` (mesma semântica, §4). */
export interface OrientableGrid {
  rpm: number[];
  load: number[];
  values_ms: number[][];
  inverted_axis?: boolean;
  inverted_rows?: boolean;
  inverted_cols?: boolean;
}

/**
 * Qual grandeza vai em cada eixo EXIBIDO — mesma regra do `orientInjectionGrid`, extraída
 * para que o heatmap 2D (grade categórica) e a superfície 3D (eixos de valor) não discordem
 * entre si ao alternar as visualizações.
 *
 * `inverted_axis: true` (caso de fábrica do Vision) → carga no eixo vertical, RPM no
 * horizontal. `false` → o oposto.
 */
export function displayAxes(m: Pick<OrientableGrid, 'inverted_axis'>): {
  xIs: GridQuantity;
  yIs: GridQuantity;
} {
  return m.inverted_axis ? { xIs: 'rpm', yIs: 'load' } : { xIs: 'load', yIs: 'rpm' };
}

function transpose(m: number[][]): number[][] {
  const rows = m.length;
  const cols = rows > 0 ? m[0].length : 0;
  const out: number[][] = [];
  for (let c = 0; c < cols; c++) {
    const row: number[] = [];
    for (let r = 0; r < rows; r++) row.push(m[r][c]);
    out.push(row);
  }
  return out;
}

/**
 * Converte o armazenamento canônico na grade **exibida**, aplicando os três flags na
 * ordem do contrato. Não ordena nada por valor: a orientação é ditada só pelos flags.
 */
export function orientInjectionGrid(m: OrientableGrid): OrientedGrid {
  // Armazenamento: linhas = rpm, colunas = load.
  let rowLabels = [...m.rpm];
  let colLabels = [...m.load];
  let grid = m.values_ms.map((r) => [...r]);
  let rowIs: GridQuantity = 'rpm';
  let colIs: GridQuantity = 'load';

  // (1) transpõe
  if (m.inverted_axis) {
    grid = transpose(grid);
    [rowLabels, colLabels] = [colLabels, rowLabels];
    [rowIs, colIs] = [colIs, rowIs];
  }

  // (2) reverte as linhas exibidas (rótulo + matriz juntos)
  if (m.inverted_rows) {
    grid = grid.slice().reverse();
    rowLabels = rowLabels.slice().reverse();
  }

  // (3) reverte as colunas exibidas (rótulo + matriz juntos)
  if (m.inverted_cols) {
    grid = grid.map((r) => r.slice().reverse());
    colLabels = colLabels.slice().reverse();
  }

  return { rowLabels, colLabels, grid, rowIs, colIs };
}

/**
 * Orienta a curva básica (`native_curve`) como matriz de 1 linha × N cargas, com os mesmos
 * três flags do mapa (§10, native_curve). Reaproveita `orientInjectionGrid` com `rpm = [0]`: essa
 * dimensão é só estrutural — no resultado, a dimensão cuja grandeza é `'rpm'` não é 1 RPM
 * nem 0 RPM, então quem renderiza NÃO deve imprimir o rótulo dela.
 *
 * `inverted_axis: false` → carga nas colunas, e só `inverted_cols` reverte a carga;
 * `true` → carga nas linhas, e só `inverted_rows` reverte a carga.
 */
export function orientNativeCurve(m: {
  native_curve: { load: number[]; display_values_ms: number[] };
  inverted_axis?: boolean;
  inverted_rows?: boolean;
  inverted_cols?: boolean;
}): OrientedGrid {
  return orientInjectionGrid({
    rpm: [0],
    load: m.native_curve.load,
    values_ms: [m.native_curve.display_values_ms],
    inverted_axis: m.inverted_axis,
    inverted_rows: m.inverted_rows,
    inverted_cols: m.inverted_cols,
  });
}

/** Rótulo do eixo de carga com a unidade da medida: MAP em bar, TPS em %; desconhecido, cru. */
export function loadAxisLabel(axis: string): string {
  if (axis === 'MAP') return 'MAP (bar)';
  if (axis === 'TPS') return 'TPS (%)';
  return axis;
}
